




























































































































































































import { defineComponent, PropType, ref, watch } from '@vue/composition-api';
import { TabletRenderer } from '@oare/oare';
import {
  EpigraphicUnitSide,
  EditTextAction,
  EditColumnPayload,
  MarkupType,
  RemoveColumnPayload,
  EpigraphicWord,
} from '@oare/types';
import EditRow from './EditRow.vue';
import InsertButton from './InsertButton.vue';
import AddRegionDialog from './AddRegionDialog.vue';
import sl from '@/serviceLocator';
import AddLineDialog from './AddLineDialog.vue';
import AddUndeterminedLinesDialog from './AddUndeterminedLinesDialog.vue';

export default defineComponent({
  props: {
    renderer: {
      type: Object as PropType<TabletRenderer>,
      required: true,
    },
    column: {
      type: Number,
      required: true,
    },
    side: {
      type: String as PropType<EpigraphicUnitSide>,
      required: true,
    },
    textUuid: {
      type: String,
      required: true,
    },
    currentEditAction: {
      type: String as PropType<EditTextAction>,
      required: false,
    },
    selectedLines: {
      type: Array as PropType<number[]>,
      required: true,
    },
    selectedWords: {
      type: Array as PropType<EpigraphicWord[]>,
      required: true,
    },
  },
  components: {
    EditRow,
    InsertButton,
    AddRegionDialog,
    AddLineDialog,
    AddUndeterminedLinesDialog,
  },
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const editTextLoading = ref(false);

    const resetRenderer = () => {
      emit('reset-renderer');
    };

    const resetCurrentEditAction = () => {
      emit('reset-current-edit-action');
    };

    const addRegionDialog = ref(false);
    watch(addRegionDialog, () => {
      if (!addRegionDialog.value) {
        resetCurrentEditAction();
      }
    });
    const addRegionPreviousLineNumber = ref<number>();
    const regionType = ref<MarkupType>();
    const setupAddRegionDialog = (
      previousLine: number | undefined,
      type: MarkupType
    ) => {
      // Can be undefined if first line
      addRegionPreviousLineNumber.value = previousLine;
      addRegionDialog.value = true;
      regionType.value = type;
    };

    const editColumnDialog = ref(false);
    const columnChangeDirection = ref<'left' | 'right'>();
    const promptEditColumn = (direction: 'left' | 'right') => {
      columnChangeDirection.value = direction;
      editColumnDialog.value = true;
    };
    const editColumn = async () => {
      try {
        editTextLoading.value = true;
        if (!columnChangeDirection.value) {
          throw new Error('No column change direction selected.');
        }
        const payload: EditColumnPayload = {
          type: 'editColumn',
          textUuid: props.textUuid,
          side: props.side,
          column: props.column,
          direction: columnChangeDirection.value,
        };
        await server.editText(payload);
        resetRenderer();
      } catch (err) {
        actions.showErrorSnackbar('Error changing column order', err as Error);
      } finally {
        editColumnDialog.value = false;
        resetCurrentEditAction();
        editTextLoading.value = false;
      }
    };

    const removeColumnDialog = ref(false);
    const removeColumn = async () => {
      try {
        editTextLoading.value = true;
        const payload: RemoveColumnPayload = {
          type: 'removeColumn',
          textUuid: props.textUuid,
          side: props.side,
          column: props.column,
        };
        await server.editText(payload);
        resetRenderer();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error removing column. Please try again.',
          err as Error
        );
      } finally {
        removeColumnDialog.value = false;
        resetCurrentEditAction();
        editTextLoading.value = false;
      }
    };
    watch(removeColumnDialog, () => {
      if (!removeColumnDialog.value) {
        resetCurrentEditAction();
      }
    });

    const handleInsertLine = (previousLine: number | undefined) => {
      if (props.currentEditAction === 'addRegionBroken') {
        setupAddRegionDialog(previousLine, 'broken');
      } else if (props.currentEditAction === 'addRegionRuling') {
        setupAddRegionDialog(previousLine, 'ruling');
      } else if (props.currentEditAction === 'addRegionSealImpression') {
        setupAddRegionDialog(previousLine, 'isSealImpression');
      } else if (props.currentEditAction === 'addRegionUninscribed') {
        setupAddRegionDialog(previousLine, 'uninscribed');
      } else if (props.currentEditAction === 'addLine') {
        setupAddLineDialog(previousLine);
      } else if (props.currentEditAction === 'addUndeterminedLines') {
        setupAddUndeterminedLinesDialog(previousLine);
      }
    };

    const addLineDialog = ref(false);
    watch(addLineDialog, () => {
      if (!addLineDialog.value) {
        resetCurrentEditAction();
      }
    });
    const addLinePreviousLineNumber = ref<number>();
    const setupAddLineDialog = (previousLine: number | undefined) => {
      // Can be undefined if first line
      addLinePreviousLineNumber.value = previousLine;
      addLineDialog.value = true;
    };

    const addUndeterminedLinesDialog = ref(false);
    watch(addUndeterminedLinesDialog, () => {
      if (!addUndeterminedLinesDialog.value) {
        addUndeterminedLinesPreviousLineNumber.value = undefined;
        resetCurrentEditAction();
      }
    });
    const addUndeterminedLinesPreviousLineNumber = ref<number>();
    const setupAddUndeterminedLinesDialog = (
      previousLine: number | undefined
    ) => {
      // Can be undefined if first line
      addUndeterminedLinesPreviousLineNumber.value = previousLine;
      addUndeterminedLinesDialog.value = true;
    };

    return {
      addRegionDialog,
      setupAddRegionDialog,
      addRegionPreviousLineNumber,
      resetRenderer,
      resetCurrentEditAction,
      editColumnDialog,
      columnChangeDirection,
      promptEditColumn,
      editTextLoading,
      editColumn,
      removeColumnDialog,
      removeColumn,
      handleInsertLine,
      addLineDialog,
      addLinePreviousLineNumber,
      setupAddLineDialog,
      addUndeterminedLinesDialog,
      addUndeterminedLinesPreviousLineNumber,
      setupAddUndeterminedLinesDialog,
      regionType,
    };
  },
});
